/**
 * =============================================================================
 * USE RFI CANDIDATE REPLIES HOOK
 * =============================================================================
 *
 * Lazy, paginated loader for the replies a staff member can link as RFI
 * evidence: APPROVED + GENERAL replies on the same question (the only replies
 * the worker accepts as link targets — §2c). Owned by `RfiManagementPanel` and
 * shared by whichever link picker is open (one question's candidates are the
 * same for every RFI on it), keeping the panel self-contained / relocatable.
 *
 * Behaviour (D-2): fetch lazily when a picker opens; "Load more" appends the
 * next page (de-duped by `replyId`); "Refresh" re-fetches page 1 and REPLACES
 * the cache (so a reply just approved in the sibling moderation panel becomes
 * linkable without reopening the modal). Hardened like `useReplies`: a stale
 * `(questionId, token)` guard (a slow response from a previous question OR a
 * rotated token can never populate the current view), cache/pagination reset on
 * question/token change, a synchronous guard against overlapping `loadMore`
 * calls, and a synchronous guard against a double initial fetch (StrictMode /
 * same-tick effect re-entry). De-dup by `replyId` is applied on EVERY mode
 * (initial / refresh / load-more), not only appends.
 *
 * Admin reply images require an authenticated blob fetch (a raw `<img src>`
 * cannot send the bearer token), so this loader is consumed by a TEXT-ONLY
 * selector (D-6) — it exposes `images[]` metadata only, never renders them.
 *
 * =============================================================================
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../api/client';
import type { AdminReplyListItem } from '@find-my-part/types';

/** The server caps `limit` at 100; request the max page size. */
const CANDIDATE_PAGE_LIMIT = 100;

/**
 * Options for the useRfiCandidateReplies hook.
 */
export interface UseRfiCandidateRepliesOptions {
  /** Question ID whose approved-GENERAL replies are link candidates. */
  questionId: string;
  /** Auth token. */
  token: string;
}

/**
 * Return value of the useRfiCandidateReplies hook.
 */
export interface UseRfiCandidateRepliesResult {
  /** Loaded APPROVED + GENERAL candidate replies (de-duped by replyId). */
  replies: AdminReplyListItem[];
  /** Whether the initial page (or a refresh) is loading. */
  isLoading: boolean;
  /** Whether a "Load more" append is in flight. */
  isFetchingMore: boolean;
  /** Error message if a load failed. */
  error: string | null;
  /** Whether more pages remain to load. */
  hasMore: boolean;
  /** Initial lazy fetch (call when the link picker opens). No-op once loaded. */
  fetchCandidates: () => Promise<void>;
  /** Append the next page. */
  loadMore: () => Promise<void>;
  /** Re-fetch page 1 and REPLACE the cache. */
  refresh: () => Promise<void>;
}

type FetchMode = 'initial' | 'more' | 'refresh';

/** Compose the full stale-guard key for a (questionId, token) pair. */
function stateKeyOf(questionId: string, token: string): string {
  return `${questionId}::${token}`;
}

/**
 * Filter a raw admin reply page to the link-eligible candidates: APPROVED
 * moderation status AND GENERAL reply type (also excludes the RFI anchor, which
 * is `RFI_REQUEST`). The worker re-validates eligibility; this is UX pre-filter.
 */
function filterCandidates(replies: AdminReplyListItem[]): AdminReplyListItem[] {
  return replies.filter(
    (reply) => reply.moderationStatus === 'APPROVED' && reply.replyType === 'GENERAL'
  );
}

/** De-duplicate replies by `replyId`, keeping first occurrence and order. */
function dedupeByReplyId(replies: AdminReplyListItem[]): AdminReplyListItem[] {
  const seen = new Set<string>();
  const out: AdminReplyListItem[] = [];
  for (const reply of replies) {
    if (seen.has(reply.replyId)) continue;
    seen.add(reply.replyId);
    out.push(reply);
  }
  return out;
}

/**
 * Hook that lazily loads a question's APPROVED-GENERAL candidate replies.
 */
export function useRfiCandidateReplies({
  questionId,
  token,
}: UseRfiCandidateRepliesOptions): UseRfiCandidateRepliesResult {
  const [replies, setReplies] = useState<AdminReplyListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Unmount protection.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Track the active (questionId, token); reset the cache/pagination when it
  // changes so a stale result can't bleed across questions or a rotated token.
  const stateKeyRef = useRef(stateKeyOf(questionId, token));
  // `true` once page 1 has been loaded for the current question (gates the
  // lazy initial fetch so reopening the picker doesn't re-hit the API).
  const hasLoadedRef = useRef(false);
  // Synchronous guard against overlapping `loadMore` appends (a shifted page
  // boundary must not double-insert).
  const loadMoreInFlightRef = useRef(false);
  // Synchronous guard against a double initial fetch (StrictMode / same-tick
  // effect re-entry) — `isLoading` is async state and can be read stale.
  const initialFetchInFlightRef = useRef(false);

  useEffect(() => {
    const stateKey = stateKeyOf(questionId, token);
    if (stateKeyRef.current !== stateKey) {
      stateKeyRef.current = stateKey;
      hasLoadedRef.current = false;
      loadMoreInFlightRef.current = false;
      initialFetchInFlightRef.current = false;
      setReplies([]);
      setPage(0);
      setTotalPages(0);
      setError(null);
    }
  }, [questionId, token]);

  const runFetch = useCallback(
    async (targetPage: number, mode: FetchMode): Promise<void> => {
      if (!token || !questionId) return;
      const startedStateKey = stateKeyOf(questionId, token);

      if (mode === 'more') {
        setIsFetchingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const response = await adminApi.replies.list(token, questionId, {
          status: 'APPROVED',
          limit: CANDIDATE_PAGE_LIMIT,
          page: targetPage,
        });
        if (!isMountedRef.current || stateKeyRef.current !== startedStateKey) return;

        if (response.success) {
          const candidates = filterCandidates(response.data.replies);
          const { page: returnedPage, totalPages: returnedTotalPages } = response.data.pagination;
          setPage(returnedPage);
          setTotalPages(returnedTotalPages);
          hasLoadedRef.current = true;

          if (mode === 'more') {
            // Append, de-duping by replyId — against the existing cache AND
            // within the new page — so neither a shifted page boundary nor a
            // repeated row can double-insert.
            setReplies((prev) => dedupeByReplyId([...prev, ...candidates]));
          } else {
            // initial + refresh both REPLACE the cache (still de-duped).
            setReplies(dedupeByReplyId(candidates));
          }
        } else {
          setError('Failed to load candidate replies. Please try again.');
        }
      } catch {
        if (!isMountedRef.current || stateKeyRef.current !== startedStateKey) return;
        setError('Failed to load candidate replies. Please try again.');
      } finally {
        // Only clear the loading flag AND the load-more in-flight guard if this
        // result is still the active view, so a stale fetch can't prematurely
        // clear a newer view's spinner or release its load-more guard (the
        // switch's reset effect already cleared the guard for the new view).
        if (isMountedRef.current && stateKeyRef.current === startedStateKey) {
          if (mode === 'more') {
            setIsFetchingMore(false);
            loadMoreInFlightRef.current = false;
          } else {
            setIsLoading(false);
          }
        }
      }
    },
    [token, questionId]
  );

  const fetchCandidates = useCallback(async (): Promise<void> => {
    // Lazy: only the first open hits the API. The synchronous in-flight ref
    // also blocks a same-tick double invocation (e.g. StrictMode re-entry).
    if (hasLoadedRef.current || initialFetchInFlightRef.current) return;
    const startedStateKey = stateKeyRef.current;
    initialFetchInFlightRef.current = true;
    try {
      await runFetch(1, 'initial');
    } finally {
      // Release the in-flight guard ONLY if still the active view, so a stale
      // initial fetch can't clear a fresh view's guard after a question/token
      // switch (the switch's reset effect already cleared it for the new view).
      if (stateKeyRef.current === startedStateKey) {
        initialFetchInFlightRef.current = false;
      }
    }
  }, [runFetch]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (loadMoreInFlightRef.current) return;
    if (page >= totalPages) return;
    loadMoreInFlightRef.current = true;
    await runFetch(page + 1, 'more');
  }, [runFetch, page, totalPages]);

  const refresh = useCallback(async (): Promise<void> => {
    await runFetch(1, 'refresh');
  }, [runFetch]);

  return {
    replies,
    isLoading,
    isFetchingMore,
    error,
    hasMore: page < totalPages,
    fetchCandidates,
    loadMore,
    refresh,
  };
}
